import { Controller } from "@hotwired/stimulus"

// Attachment bulk-download picker for one settlements section. A "Download"
// button opens a <dialog> listing that section's loads; the user picks loads
// (click, click-and-drag with the mouse, a date range, or select-all) and
// submits the form, which posts the checked document_no's to
// OrganizationsController#download_attachments and streams back a zip.
//
// During a mouse drag, if the pointer nears the top/bottom edge of the list the
// list auto-scrolls and keeps extending the selection, so the user never has to
// stop and scroll by hand.
export default class extends Controller {
  static targets = ["dialog", "list", "item", "checkbox", "all", "from", "to", "count", "submit", "progress", "progressFill", "progressLabel", "closeButton"]

  connect() {
    this.dragging = false
    this.lastPointerMouse = false
    this.pointerX = 0
    this.pointerY = 0
    this.autoScrollRAF = null

    this.endDrag = () => this.stopDrag()
    this.trackPointer = (event) => {
      this.pointerX = event.clientX
      this.pointerY = event.clientY
    }
    window.addEventListener("pointerup", this.endDrag)
    window.addEventListener("pointermove", this.trackPointer)

    // Restore page scroll whenever the dialog closes (Esc, backdrop, or button).
    this.onDialogClose = () => this.scheduleUnlock()
    // Esc while downloading: keep the dialog open and route through the same
    // confirm-to-leave prompt as the ✕ / backdrop.
    this.onDialogCancel = (event) => {
      if (this.downloading) {
        event.preventDefault()
        this.confirmLeave()
      }
    }
    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener("close", this.onDialogClose)
      this.dialogTarget.addEventListener("cancel", this.onDialogCancel)
    }

    // Warn on leaving the whole page mid-download. beforeunload covers a full
    // reload / tab close; turbo:before-visit covers in-app (Turbo) navigation.
    this.onBeforeUnload = (event) => {
      if (this.downloading) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    this.onTurboVisit = (event) => {
      if (!this.downloading) return
      if (window.confirm("Leaving will cancel the download in progress. Leave anyway?")) {
        this.cancel()
      } else {
        event.preventDefault()
      }
    }
    window.addEventListener("beforeunload", this.onBeforeUnload)
    document.addEventListener("turbo:before-visit", this.onTurboVisit)

    this.update()
  }

  disconnect() {
    window.removeEventListener("pointerup", this.endDrag)
    window.removeEventListener("pointermove", this.trackPointer)
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.onTurboVisit)
    if (this.hasDialogTarget) {
      this.dialogTarget.removeEventListener("close", this.onDialogClose)
      this.dialogTarget.removeEventListener("cancel", this.onDialogCancel)
    }
    this.stopAutoScroll()
    if (this.unlockTimer) clearTimeout(this.unlockTimer)
    this.unlockScroll()
  }

  open() {
    this.syncSelectedClasses()
    this.update()
    this.lockScroll()
    this.dialogTarget.showModal()
  }

  close() {
    this.dialogTarget.close()
  }

  // Header ✕: confirm-to-leave while downloading, otherwise just close.
  requestClose() {
    if (this.downloading) {
      this.confirmLeave()
    } else {
      this.close()
    }
  }

  // Prompt before abandoning an in-flight download; on confirm, cancel it and
  // close the modal.
  confirmLeave() {
    if (window.confirm("Leaving will cancel the download in progress. Exit anyway?")) {
      this.cancel()
      this.close()
    }
  }

  lockScroll() {
    if (this.unlockTimer) {
      clearTimeout(this.unlockTimer)
      this.unlockTimer = null
    }
    // Compensate for the vanishing scrollbar so the page doesn't shift right
    // when we hide overflow.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    this.prevBodyOverflow = document.body.style.overflow
    this.prevBodyPaddingRight = document.body.style.paddingRight
    document.body.style.overflow = "hidden"
    if (scrollbarWidth > 0) {
      const currentPadding = parseFloat(getComputedStyle(document.body).paddingRight) || 0
      document.body.style.paddingRight = `${currentPadding + scrollbarWidth}px`
    }
  }

  // Wait for the exit animation to finish before restoring the scrollbar, so it
  // doesn't reappear (and shift the page) while the modal is still fading out.
  // Reduced-motion users have no animation, so restore immediately.
  scheduleUnlock() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.unlockScroll()
      return
    }
    if (this.unlockTimer) clearTimeout(this.unlockTimer)
    this.unlockTimer = setTimeout(() => {
      this.unlockTimer = null
      this.unlockScroll()
    }, 200)
  }

  unlockScroll() {
    document.body.style.overflow = this.prevBodyOverflow || ""
    document.body.style.paddingRight = this.prevBodyPaddingRight || ""
  }

  // Close when the click lands on the dialog element itself (the backdrop),
  // not on its inner content.
  backdropClose(event) {
    if (event.target !== this.dialogTarget) return
    if (this.downloading) {
      this.confirmLeave()
    } else {
      this.close()
    }
  }

  // Mouse: begin a drag-select and toggle the anchor. Touch/pen: fall through
  // to itemClick (a tap) so list scrolling isn't hijacked.
  dragStart(event) {
    this.lastPointerMouse = event.pointerType === "mouse"
    if (!this.lastPointerMouse) return

    const item = event.target.closest("[data-bulk-download-target='item']")
    if (!item) return

    event.preventDefault()
    this.dragging = true
    this.pointerX = event.clientX
    this.pointerY = event.clientY
    this.anchorIndex = this.itemTargets.indexOf(item)
    this.dragState = !this.checkboxFor(item).checked
    this.setChecked(item, this.dragState)
    this.update()
    this.startAutoScroll()
  }

  dragOver(event) {
    if (!this.dragging || event.pointerType !== "mouse") return
    const item = event.target.closest("[data-bulk-download-target='item']")
    if (!item) return
    this.selectRange(this.anchorIndex, this.itemTargets.indexOf(item), this.dragState)
    this.update()
  }

  // Mouse selection is already handled in dragStart, so swallow the click.
  // Touch taps and keyboard activations toggle the item here.
  itemClick(event) {
    if (this.lastPointerMouse) {
      this.lastPointerMouse = false
      return
    }
    const item = event.target.closest("[data-bulk-download-target='item']")
    if (!item) return
    this.setChecked(item, !this.checkboxFor(item).checked)
    this.update()
  }

  toggleAll() {
    this.itemTargets.forEach((item) => this.setChecked(item, this.allTarget.checked))
    this.update()
  }

  // Select every load whose date falls in the chosen range (inclusive), and
  // deselect the rest. Does nothing until at least one bound is set.
  filterByDate() {
    const from = this.fromTarget.value
    const to = this.toTarget.value
    if (!from && !to) return

    this.itemTargets.forEach((item) => {
      const date = item.dataset.date
      let inRange = Boolean(date)
      if (date && from && date < from) inRange = false
      if (date && to && date > to) inRange = false
      this.setChecked(item, inRange)
    })
    this.update()
  }

  // --- client-side download with progress ---

  // Intercepts the form submit: enqueue a server-side job that builds the zip
  // off the web threads, poll its progress, then download the finished zip. The
  // no-JS path (form POST -> download_attachments) still exists as a fallback.
  async download(event) {
    event.preventDefault()
    if (this.downloading) return

    const form = event.currentTarget
    const docNos = [...new Set(
      this.itemTargets.filter((item) => this.checkboxFor(item).checked).map((item) => this.checkboxFor(item).value)
    )]
    if (docNos.length === 0) return

    const source = form.elements.source ? form.elements.source.value : "received"
    const blanketOrderNo = form.elements.blanket_order_no ? form.elements.blanket_order_no.value : ""
    const enqueueUrl = form.dataset.bulkDownloadUrl
    const statusUrl = form.dataset.bulkDownloadStatusUrl

    this.downloading = true
    this.abortController = new AbortController()
    const signal = this.abortController.signal
    this.setDownloadingUI(true)

    try {
      const params = new URLSearchParams()
      params.set("source", source)
      if (blanketOrderNo) params.set("blanket_order_no", blanketOrderNo)
      docNos.forEach((no) => params.append("document_nos[]", no))

      // Kick off the job; it returns a token we poll for progress.
      const started = await fetch(enqueueUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json"
        },
        body: params.toString(),
        signal
      }).then((r) => {
        if (!r.ok) throw new Error(`enqueue ${r.status}`)
        return r.json()
      })

      const result = await this.pollStatus(statusUrl, started.id, signal)

      if (result.state !== "done") {
        this.setDownloadingUI(false)
        this.showMessage(result.error || "Download failed — please try again.")
        return
      }

      // Fetch the finished zip into a blob and download from an object URL
      // (rather than navigating the browser to the endpoint) — a plain link
      // navigation to a binary attachment is unreliable across browsers.
      this.showProgressLabel("Downloading…")
      const blob = await fetch(result.download_url, { signal }).then((r) => {
        if (!r.ok) throw new Error(`download ${r.status}`)
        return r.blob()
      })
      this.triggerBlobDownload(blob, result.filename || "attachments.zip")
      this.setDownloadingUI(false)
      if (result.skipped > 0) {
        this.showMessage(`Download ready — ${result.skipped} file(s) could not be retrieved and were left out.`)
      } else {
        this.close()
      }
    } catch (error) {
      this.setDownloadingUI(false)
      if (error.name !== "AbortError") {
        this.showMessage("Download failed — please try again.")
      }
    } finally {
      this.downloading = false
      this.abortController = null
    }
  }

  // Poll the status endpoint until the job reports done or error, updating the
  // progress bar as files complete. Rejects with AbortError if the user cancels.
  // Safety cap: if the status stops changing for a while (e.g. the job died mid-
  // run during a deploy/restart), give up with an error instead of polling forever.
  async pollStatus(statusUrl, id, signal) {
    const url = `${statusUrl}?token=${encodeURIComponent(id)}`
    const STALL_MS = 5 * 60 * 1000
    let lastDone = -1
    let lastState = null
    let lastProgressAt = Date.now()

    while (true) {
      if (signal.aborted) throw this.abortError()

      const status = await fetch(url, { headers: { Accept: "application/json" }, signal }).then((r) => r.json())
      if (status.total > 0) this.setProgress(status.done || 0, status.total)
      if (status.state === "done" || status.state === "error") return status

      // Reset the stall timer whenever progress or state changes; bail if it
      // hasn't moved for STALL_MS (job likely died without finishing).
      const done = status.done || 0
      if (done !== lastDone || status.state !== lastState) {
        lastDone = done
        lastState = status.state
        lastProgressAt = Date.now()
      } else if (Date.now() - lastProgressAt > STALL_MS) {
        return { state: "error", error: "This download took too long — please try again." }
      }

      await this.delay(1200, signal)
    }
  }

  // Promise that resolves after ms, or rejects immediately if the signal aborts.
  delay(ms, signal) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, ms)
      if (signal) {
        signal.addEventListener("abort", () => {
          clearTimeout(timer)
          reject(this.abortError())
        }, { once: true })
      }
    })
  }

  abortError() {
    const error = new Error("aborted")
    error.name = "AbortError"
    return error
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  // Trigger a download from an in-memory blob via a temporary object URL — no
  // network navigation, so it works consistently across browsers.
  triggerBlobDownload(blob, filename) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  // Abort the in-flight download; the fetch loop rejects with AbortError, which
  // the download() catch treats as a silent cancel and resets the UI.
  cancel() {
    if (this.abortController) this.abortController.abort()
  }

  // Shows the progress bar + cancel and hides the picker chrome; when inactive,
  // hides and fully resets the progress UI so nothing stale lingers.
  setDownloadingUI(active) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = active
      this.submitTarget.value = active ? "Preparing…" : "Download selected"
    }
    if (this.hasProgressTarget) this.progressTarget.hidden = !active
    if (this.hasCountTarget) this.countTarget.hidden = active

    if (active) {
      if (this.hasProgressFillTarget) this.progressFillTarget.style.width = "0%"
      this.showProgressLabel("")
    } else {
      if (this.hasProgressFillTarget) this.progressFillTarget.style.width = "0%"
      this.showProgressLabel("")
      this.update()
    }
  }

  showMessage(text) {
    if (this.hasCountTarget) this.countTarget.textContent = text
  }

  setProgress(done, total) {
    const pct = total > 0 ? Math.round((done / total) * 100) : 0
    if (this.hasProgressFillTarget) this.progressFillTarget.style.width = `${pct}%`
    this.showProgressLabel(`${done} / ${total} files`)
  }

  showProgressLabel(text) {
    if (this.hasProgressLabelTarget) this.progressLabelTarget.textContent = text
  }

  // --- drag auto-scroll ---

  startAutoScroll() {
    if (this.autoScrollRAF) return
    const step = () => {
      if (!this.dragging) {
        this.autoScrollRAF = null
        return
      }
      this.autoScrollStep()
      this.autoScrollRAF = requestAnimationFrame(step)
    }
    this.autoScrollRAF = requestAnimationFrame(step)
  }

  stopAutoScroll() {
    if (this.autoScrollRAF) {
      cancelAnimationFrame(this.autoScrollRAF)
      this.autoScrollRAF = null
    }
  }

  autoScrollStep() {
    if (!this.hasListTarget) return
    const list = this.listTarget
    const rect = list.getBoundingClientRect()
    const edge = 44
    const maxSpeed = 16

    let dy = 0
    if (this.pointerY < rect.top + edge) {
      dy = -maxSpeed * Math.min(1, (rect.top + edge - this.pointerY) / edge)
    } else if (this.pointerY > rect.bottom - edge) {
      dy = maxSpeed * Math.min(1, (this.pointerY - (rect.bottom - edge)) / edge)
    }
    if (dy === 0) return

    const before = list.scrollTop
    list.scrollTop += dy
    if (list.scrollTop === before) return // already at top/bottom

    this.extendToPointer()
  }

  // Extend the drag selection to whatever item sits under the pointer, clamping
  // the point into the list so an item is still hit when the cursor has run past
  // the edge (which is exactly when auto-scroll kicks in).
  extendToPointer() {
    if (!this.hasListTarget) return
    const rect = this.listTarget.getBoundingClientRect()
    const x = Math.min(Math.max(this.pointerX, rect.left + 1), rect.right - 1)
    const y = Math.min(Math.max(this.pointerY, rect.top + 1), rect.bottom - 1)

    const el = document.elementFromPoint(x, y)
    if (!el) return
    const item = el.closest("[data-bulk-download-target='item']")
    if (!item || !this.itemTargets.includes(item)) return

    this.selectRange(this.anchorIndex, this.itemTargets.indexOf(item), this.dragState)
    this.update()
  }

  stopDrag() {
    this.dragging = false
    this.stopAutoScroll()
  }

  // --- helpers ---

  checkboxFor(item) {
    return item.querySelector("input[type=checkbox]")
  }

  setChecked(item, state) {
    this.checkboxFor(item).checked = state
    item.classList.toggle("is-selected", state)
  }

  selectRange(a, b, state) {
    const lo = Math.min(a, b)
    const hi = Math.max(a, b)
    for (let i = lo; i <= hi; i++) this.setChecked(this.itemTargets[i], state)
  }

  syncSelectedClasses() {
    this.itemTargets.forEach((item) => {
      item.classList.toggle("is-selected", this.checkboxFor(item).checked)
    })
  }

  update() {
    const total = this.itemTargets.length
    const checked = this.itemTargets.filter((item) => this.checkboxFor(item).checked).length

    // Don't re-enable the submit button mid-download (e.g. if update() runs from
    // a reopen) — setDownloadingUI owns its state while downloading.
    if (this.hasSubmitTarget && !this.downloading) this.submitTarget.disabled = checked === 0
    if (this.hasAllTarget) {
      // Only checked (blue) when everything is selected; a partial selection
      // leaves it unchecked/white rather than showing the confusing dash state.
      this.allTarget.indeterminate = false
      this.allTarget.checked = total > 0 && checked === total
    }
    if (this.hasCountTarget) {
      this.countTarget.textContent = checked > 0 ? `${checked} selected` : "None selected"
    }
  }
}
